<!-- src/views/admin/AdminOnboardingDashboardView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & INTEGRATED COMMAND FILTERS -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden text-left">
          <div class="absolute top-0 right-0 p-12 opacity-5"><IdentificationIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Onboarding Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 text-left">Registry Intelligence • Workforce Readiness & Compliance</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <!-- 🛡️ PERMISSION HARDENED: Changed label to "Manage Options" and opened access to all Admins -->
                <router-link v-if="canManageOptions" to="/admin/manage-onboarding-form-options" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2">
                    <AdjustmentsHorizontalIcon class="h-4 w-4" /> <span>Manage Options</span>
                </router-link>

                <!-- 🛡️ PERMISSION HARDENED: Opened access to all Admins -->
                <router-link v-if="canManageOptions" to="/admin/manage-onboarding-form-text" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg">
                    <DocumentTextIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Manage Text</span>
                </router-link>

                <!-- 🛡️ NAVIGATION STANDARDIZED: Back button label updated from 'Admin' to 'Return' -->
                <router-link to="/client-admin" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
                  <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                </router-link>
              </div>
            </div>

            <!-- INTEGRATED FILTER ROW -->
            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-6 rounded-[32px] border border-white/10 text-left">
              <div class="flex flex-wrap gap-4 items-center flex-grow">
                <div class="flex flex-col gap-1.5 min-w-[240px] flex-grow sm:flex-none">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Registry</span>
                  <input v-model="filters.search" type="text" placeholder="Reference or Name..." class="filter-input-dark" />
                </div>

                <div class="flex flex-col gap-1.5 min-w-[240px] flex-grow sm:flex-none">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Form Registry Selection</span>
                  <select v-model="activeTab" class="filter-select-dark">
                    <option v-for="tab in formTabs" :key="tab.id" :value="tab.id">
                      {{ tab.label }} ({{ tabCounts[tab.id] || 0 }})
                    </option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 min-w-[160px] flex-grow sm:flex-none">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Review Status Filter</span>
                  <select v-model="filters.status" class="filter-select-dark">
                    <option value="all">All Statuses</option>
                    <option value="Reviewed">Reviewed / Confirmed</option>
                    <option value="Pending Review">Pending Review</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Requires Action">Requires Action</option>
                  </select>
                </div>
              </div>

              <button 
                @click="exportToExcel"
                :disabled="exporting || filteredSubmissions.length === 0"
                class="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-white/20 transition-all flex items-center gap-3 disabled:opacity-50 shadow-xl"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                  <span>Syncing...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 🛡️ 2. SENTINEL INTELLIGENCE: DETERMINISTIC SYNTHESIS HUB -->
        <div class="mb-12 animate-fadeIn text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative text-left">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4 text-left">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl text-left">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div class="text-left">
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none text-left">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2 text-left">Active Onboarding Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesizing...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left">
                 <div v-for="stat in onboardingStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current onboarding submissions for patterns, compliance, and friction points.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Onboarding Pipelines</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Last 50 Records
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Form Data & Synthesizing Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 4. DATA DISPLAY -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hydrating Onboarding Registry...</p>
        </div>

        <div v-else-if="filteredSubmissions.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 animate-fadeIn shadow-inner">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No records identified for the selected form type.</p>
        </div>

        <div v-else class="space-y-4 animate-fadeIn text-left">
          <div 
            v-for="sub in processedPaginatedSubmissions" 
            :key="sub.id"
            class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group"
          >
            <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
              
              <!-- QR VERIFICATION & REFERENCE -->
              <div class="flex items-center gap-6 w-full xl:w-[25%] shrink-0 text-left">
                <div class="relative h-20 w-20 shrink-0 bg-white p-1 rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
                  <img v-if="sub.qrCodeUrl" :src="sub.qrCodeUrl" alt="QR" class="w-full h-full object-contain" />
                  <div v-else class="animate-pulse bg-slate-50 w-full h-full rounded-lg"></div>
                </div>
                <div class="min-w-0">
                  <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">
                    {{ sub.humanReadableId || 'REF-TBC' }}
                  </h3>
                </div>
              </div>

              <!-- SUBMITTER IDENTITY & DATE -->
              <div class="flex-grow px-4 border-l border-slate-50 min-w-0 flex items-baseline gap-4 text-left">
                <h4 class="text-sm font-black text-[#0A1630] uppercase leading-none truncate text-left">
                   {{ sub.processedUserName || 'Personnel' }}
                </h4>
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                   {{ formatDate(getSubmissionAt(sub)) }}
                </span>
              </div>

              <!-- STATUS & ACTIONS -->
              <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                 <div class="text-right shrink-0">
                    <span
                      :class="statusClass(getReviewStatus(sub))"
                      class="px-5 py-2 inline-flex text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm"
                    >
                      {{ getReviewStatus(sub) }}
                    </span>
                 </div>

                 <router-link 
                  :to="{ name: 'completedForm', params: { collectionName: 'onboardingFormSubmissions', submissionId: sub.id }}" 
                  class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                >
                  <EyeIcon class="h-4 w-4" /> <span>Review Full Audit</span>
                </router-link>
              </div>
            </div>
          </div>
          
          <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
          <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
            <div class="flex items-center gap-4">
              <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                &larr; Prev
              </button>
              <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
                Page {{ currentPage }} of {{ totalPages || 1 }}
              </span>
              <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages || totalPages === 0"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                Next &rarr;
              </button>
            </div>
            
            <!-- Symmetrical Neon-Chevron Items Per Page Selector (Forced h-11 height) -->
            <div class="flex items-center gap-3">
              <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
              <select 
                v-model="itemsPerPage" 
                class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
                style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
              >
                <option :value="25">25 Rows</option>
                <option :value="50">50 Rows</option>
                <option :value="100">100 Rows</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import { 
  IdentificationIcon, ArrowUturnLeftIcon,
  AdjustmentsHorizontalIcon, EyeIcon, TableCellsIcon, MagnifyingGlassIcon,
  DocumentTextIcon, UserIcon, AcademicCapIcon, ClipboardDocumentCheckIcon,
  BookOpenIcon, BriefcaseIcon, HeartIcon, GlobeAltIcon,
  ShieldCheckIcon, BuildingOfficeIcon, ExclamationTriangleIcon,
  ClockIcon, SparklesIcon, CpuChipIcon, SignalIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const functions = getFunctions(auth.app, 'europe-west2');

const loading = ref(true);
const exporting = ref(false);
const allSubmissions = ref([]); 

// 🛡️ STICKY FILTERS STORAGE KEYS
const STORAGE_TAB_KEY = 'sentinel_onboarding_active_tab';
const STORAGE_SEARCH_KEY = 'sentinel_onboarding_search';
const STORAGE_STATUS_KEY = 'sentinel_onboarding_status';
const STORAGE_LIMIT_KEY = 'sentinel_onboarding_limit';

const activeTab = ref(sessionStorage.getItem(STORAGE_TAB_KEY) || 'yourDetails'); 
const tabCounts = reactive({});

// PAGINATION STATE (DYNAMIC REFERENCE)
const currentPage = ref(1);
const itemsPerPage = ref(Number(sessionStorage.getItem(STORAGE_LIMIT_KEY)) || 25);
const processedPaginatedSubmissions = ref([]);

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

const filters = reactive({
  search: sessionStorage.getItem(STORAGE_SEARCH_KEY) || '',
  status: sessionStorage.getItem(STORAGE_STATUS_KEY) || 'all'
});

// Race-condition guard token
let currentExecutionId = 0;

/** 🛡️ FORM TABS REGISTRY: Realigned to match Onboarding Forms order and labels */
const formTabs = [
  { id: 'yourDetails', label: 'Your Details', icon: UserIcon },
  { id: 'employeeInduction', label: 'Induction', icon: AcademicCapIcon },
  { id: 'rolesAndResponsibilities', label: 'Responsibility Acknowledgement', icon: ClipboardDocumentCheckIcon },
  { id: 'literacyTest', label: 'Literacy Test', icon: BookOpenIcon },
  { id: 'annualHealthAssessment', label: 'Health Assessment', icon: HeartIcon },
  { id: 'eyesightDeclaration', label: 'Eyesight Declaration', icon: EyeIcon },
  { id: 'rightToWork', label: 'Right to Work', icon: GlobeAltIcon },
  { id: 'ppeRequest', label: 'PPE Issue', icon: ShieldCheckIcon },
  { id: 'primarySponsorContract', label: 'Primary Sponsor Contract', icon: BriefcaseIcon },
  { id: 'employeeHandbookSignOff', label: 'Employee Handbook Acknowledgement', icon: BookOpenIcon },
  { id: 'subContractorPack', label: 'Sub-Contractor Pack', icon: BuildingOfficeIcon }
];

const getReviewStatus = (sub) => {
  const status = sub.status || 'Pending Review';
  if (status === 'Pending') return 'Pending Review';
  if (status === 'Completed' && sub.formIdentifier !== 'supportRequestSubmissions') return 'Reviewed';
  return status;
};

const getSubmissionAt = (sub) => sub.submittedAt || sub.formData?.submittedAt || null;

const statusClass = (status) => {
  const s = status.toUpperCase();
  if (s === 'REVIEWED' || s === 'COMPLETED') return 'bg-[#EBFDF8] text-[#0A1630] border border-[#20FCF1]/30';
  if (s === 'REQUIRES ACTION') return 'bg-rose-50 text-rose-700 border border-rose-200';
  if (s === 'IN PROGRESS') return 'bg-blue-50 text-blue-700 border border-blue-200';
  return 'bg-amber-50 text-amber-700 border border-amber-200';
};

const onboardingStats = computed(() => {
  const currentCategorySubs = allSubmissions.value.filter(s => s.formIdentifier === activeTab.value);
  
  const pendingCount = currentCategorySubs.filter(s => getReviewStatus(s) === 'Pending Review').length;
  const actionCount = currentCategorySubs.filter(s => getReviewStatus(s) === 'Requires Action').length;
  const inProgressCount = currentCategorySubs.filter(s => getReviewStatus(s) === 'In Progress').length;

  return [
    { label: 'Registry Total', value: currentCategorySubs.length, color: '#20FCF1', icon: AcademicCapIcon },
    { label: 'Pending Review', value: pendingCount, color: '#FBBF24', icon: ClipboardDocumentCheckIcon },
    { label: 'Requires Action', value: actionCount, color: '#EF4444', icon: ExclamationTriangleIcon },
    { label: 'In Progress', value: inProgressCount, color: '#3B82F6', icon: ClockIcon },
  ];
});

const filteredSubmissions = computed(() => {
  return allSubmissions.value.filter(s => {
    const matchTab = s.formIdentifier === activeTab.value;
    const matchSearch = !filters.search || 
      (s.userName || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      (s.humanReadableId || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      (s.companyInfo?.companyName || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      (s.companyName || '').toLowerCase().includes(filters.search.toLowerCase());
    
    const statusVal = getReviewStatus(s).toLowerCase();
    const matchStatus = filters.status === 'all' || statusVal === filters.status.toLowerCase();
    
    return matchTab && matchSearch && matchStatus;
  });
});

// PAGINATION MATH & CONTROL PROTOCOLS
const totalPages = computed(() => {
  return Math.ceil(filteredSubmissions.value.length / itemsPerPage.value);
});

const paginatedSubmissions = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredSubmissions.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

// Lazy-loads profile details & QR code only for the visible items on the active page
const processActivePage = async () => {
  currentExecutionId++;
  const execId = currentExecutionId;
  const items = paginatedSubmissions.value;
  
  // Set fast temporary display states to ensure instant UI response
  processedPaginatedSubmissions.value = items.map(sub => ({
    ...sub,
    processedUserName: sub.userName || 'Personnel',
    qrCodeUrl: null
  }));

  // Resolve profile names and QR codes asynchronously in the background
  const resolved = await Promise.all(items.map(async (docData) => {
    let resolvedName = docData.userName || 'Personnel';
    
    // 🛡️ SUB-CONTRACTOR PACK FIX: Strictly enforce company name precedence and bypass personal profile user lookup overwrite
    if (docData.formIdentifier === 'subContractorPack') {
        resolvedName = docData.companyInfo?.companyName || 
                       docData.companyName || 
                       docData.formData?.companyInfo?.companyName || 
                       docData.userName || 
                       'Corporate Entity';
    } else {
      const submitterId = docData.userId || docData.submittedBy;
      if (submitterId) {
        try {
            const pSnap = await getDoc(doc(db, 'userProfiles', submitterId));
            if (pSnap.exists()) {
                const up = pSnap.data();
                if (up.employmentType === 'sub-contractor') {
                    resolvedName = up.firstName;
                } else {
                    resolvedName = `${up.firstName || ''} ${up.lastName || ''}`.trim();
                }
            }
        } catch (e) { /* Silent skip */ }
      }
    }

    const url = `${window.location.origin}/completed-form/onboardingFormSubmissions/${docData.id}`;
    let qrUrl = null;
    try { qrUrl = await QRCode.toDataURL(url, { width: 200, margin: 1 }); } catch (e) { console.error(e); }
    
    return { ...docData, processedUserName: resolvedName, qrCodeUrl: qrUrl };
  }));

  // Strict execution ID guard blocks race conditions when switching tabs rapidly
  if (execId === currentExecutionId) {
    processedPaginatedSubmissions.value = resolved;
  }
};

// 🛡️ DETERMINISTIC SYNTHESIS ENGINE
const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) { alert("Identity context missing."); isAiLoading.value = false; return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const last50 = allSubmissions.value.slice(0, 50);
        const pendingCount = last50.filter(s => getReviewStatus(s) === 'Pending Review').length;
        const actionRequiredCount = last50.filter(s => getReviewStatus(s) === 'Requires Action').length;
        const reviewedCount = last50.filter(s => getReviewStatus(s) === 'Reviewed' || getReviewStatus(s) === 'Completed').length;
        
        const forms = last50.map(s => s.formIdentifier).filter(Boolean);
        const formCounts = forms.reduce((acc, f) => {
          acc[f] = (acc[f] || 0) + 1;
          return acc;
        }, {});
        const topFormId = Object.keys(formCounts).sort((a,b) => formCounts[b] - formCounts[a])[0] || 'yourDetails';
        const topFormLabel = formTabs.find(t => t.id === topFormId)?.label || 'Onboarding Registry';

        const readinessRate = last50.length > 0 ? Math.round((reviewedCount / last50.length) * 100) : 100;

        const report = {
            summary: `Workforce readiness audit of current onboarding submissions indicates an operational maturity index of ${readinessRate}%. Out of the last 50 entries, ${reviewedCount} are fully verified. Active registration flow is heavily concentrated on ${topFormLabel} filings.`,
            trends: [
                { label: 'Review Friction', value: `${pendingCount} Forms Pending HR Verification`, isCritical: pendingCount > 5 },
                { label: 'Compliance Roadblocks', value: `${actionRequiredCount} Submissions Flagged 'Requires Action'`, isCritical: actionRequiredCount > 0 },
                { label: 'Active Pipeline Flow', value: `High volume of ${topFormLabel}`, isCritical: false }
            ],
            actions: [
                { type: 'Verification Push', instruction: `Clear ${pendingCount} pending reviews`, reason: 'Forms must be verified to authorize active roster placement.' },
                { type: 'Friction Resolution', instruction: `Re-evaluate ${actionRequiredCount} flagged forms`, reason: 'Profiles marked with Requires Action block operational onboarding sequences.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'onboarding_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'onboarding_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Onboarding Intelligence Registry Neutral.");
  }
};

const fetchData = async () => {
  loading.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) return;

    await fetchLatestAiReport(cid);

    const q = query(collection(db, 'onboardingFormSubmissions'), where('clientId', '==', cid));
    const snap = await getDocs(q);
    
    // Direct, synchronous mapped fetch has zero hydration bottlenecks
    allSubmissions.value = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return dateB - dateA;
    });

    formTabs.forEach(tab => {
       tabCounts[tab.id] = allSubmissions.value.filter(d => d.formIdentifier === tab.id).length;
    });

    const result = await httpsCallable(functions, 'getAnalyticsData')();
    console.log("Analytics Synced:", !!result.data);
  } catch (err) { console.error("Onboarding Sync Error:", err); } finally { loading.value = false; }
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  const date = val.toDate ? val.toDate() : new Date(val);
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const formatHeader = (key) => key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const exportToExcel = () => {
  exporting.value = true;
  try {
    const dataToExport = filteredSubmissions.value.map(sub => {
      const row = {
        'Reference': sub.humanReadableId,
        'Personnel': sub.userName,
        'Review Status': getReviewStatus(sub),
        'Submitted': formatDate(getSubmissionAt(sub))
      };
      const formData = sub.formData || {};
      Object.keys(formData).forEach(k => {
        if (!['signature', 'photo', 'url', 'base64'].some(s => k.toLowerCase().includes(s))) {
          row[formatHeader(k)] = formData[k];
        }
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Onboarding_Registry");
    XLSX.writeFile(wb, `Sentinel_Onboarding_Export_${activeTab.value}.xlsx`);
  } catch (err) { alert(err.message); } finally { exporting.value = false; }
};

onMounted(() => { if (userStore.isAuthReady) fetchData(); });

// Watchers to trigger lazy-loading & reset current page back to 1 on tab/filter/display updates
watch(paginatedSubmissions, () => {
  processActivePage();
}, { immediate: true });

// 🛡️ STICKY FILTERS WATCHERS: Persist states to sessionStorage
watch(activeTab, (val) => {
  sessionStorage.setItem(STORAGE_TAB_KEY, val);
  currentPage.value = 1;
});

watch(() => filters.search, (val) => {
  sessionStorage.setItem(STORAGE_SEARCH_KEY, val);
  currentPage.value = 1;
});

watch(() => filters.status, (val) => {
  sessionStorage.setItem(STORAGE_STATUS_KEY, val);
  currentPage.value = 1;
});

watch(itemsPerPage, (val) => {
  sessionStorage.setItem(STORAGE_LIMIT_KEY, val);
  currentPage.value = 1;
});

/**
 * 🛡️ TOTAL COMMAND PROTOCOL: canManageOptions
 * Any Admin authenticated within this Command Centre is granted full 
 * access to manage Options and Text, satisfying the requirement that 
 * access to the registry equals access to the tools.
 */
const canManageOptions = computed(() => 
  userStore.isAdmin || 
  userStore.isManager || 
  userStore.permissions?.canManageOnboarding
);
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500 shadow-inner; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
}
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.no-scrollbar::-webkit-scrollbar { display: none; }
</style>